import { Dispatch, SetStateAction, useCallback, useEffect, useState } from "react";
import { useGetMyOrdersQuery } from "../../../../../../../API/authApi";
import { IOrderResponse, IMyOrdersResponse } from "../../../../../../../Interfaces/responseTypes";
import { VisitorPageContents } from "../../../../visitorPage";

export enum MyOrdersContentTypes {
  ORDER_LIST = "list",
  ORDER_ITEM = "item",
}

const useMyOrdersHook = (setContentType: Dispatch<SetStateAction<VisitorPageContents>>) => {
  const storedPhone = localStorage.getItem("phone") || "";
  const phone = storedPhone.replace(/^\+/, "");
  const user_uuid = localStorage.getItem("user_id");

  const [activeContent, setActiveContent] = useState<MyOrdersContentTypes>(
    MyOrdersContentTypes.ORDER_LIST
  );
  const [page, setPage] = useState(1);
  const [orders, setOrders] = useState<IOrderResponse[]>([]);
  const [hasNext, setHasNext] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<IOrderResponse | null>(null);
  const [isTabVisible, setIsTabVisible] = useState(!document.hidden);

  const { data: firstPageData, error, refetch } = useGetMyOrdersQuery(
    { phone, page: 1, user_uuid },
    {
      skip: !phone,
      pollingInterval: isTabVisible ? 30000 : 0,
    }
  );

  const { data, isFetching, isError } = useGetMyOrdersQuery(
    { phone, page, user_uuid },
    { skip: !phone || (!hasNext && page > 1) }
  );

  useEffect(() => {
    if (!data) return;
    const { results, next } = data as IMyOrdersResponse;

    setOrders((prev) => {
      if (page === 1) return results;
      const newOrders = results.filter((d) => !prev.some((p) => p.id === d.id));
      return [...prev, ...newOrders];
    });

    setHasNext(!!next);
  }, [data, page]);

  useEffect(() => {
    if (!firstPageData) return;
    const { results } = firstPageData as IMyOrdersResponse;

    setOrders((prev) => {
      const rest = prev.filter((o) => !results.some((r) => r.id === o.id));
      return [...results, ...rest];
    });
  }, [firstPageData]);

  useEffect(() => {
    if (error && (error as { error: number }).error === 405) {
      localStorage.removeItem("user_id");
      localStorage.removeItem("phone");
      setContentType(VisitorPageContents.MENU)
    }
  }, [error]);

  // ---------------- Tab Visibility Detection ----------------
  useEffect(() => {
    const handleVisibilityChange = () => {
      const wasHidden = !isTabVisible;
      const isNowVisible = !document.hidden;
      
      setIsTabVisible(isNowVisible);
      
      // If tab becomes visible and phone exists, fetch immediately
      if (wasHidden && isNowVisible && phone) {
        refetch();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isTabVisible, phone, refetch]);

  const [sentinelEl, setSentinelEl] = useState<HTMLDivElement | null>(null);
  const sentinelRef = useCallback((node: HTMLDivElement | null) => setSentinelEl(node), []);

  const observerCallback = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      const entry = entries[0];
      if (entry?.isIntersecting && !isFetching && hasNext) {
        setPage((prev) => prev + 1);
      }
    },
    [isFetching, hasNext]
  );

  useEffect(() => {
    if (!sentinelEl) return;

    const observer = new IntersectionObserver(observerCallback, {
      root: null,
      rootMargin: "200px",
      threshold: 0.1,
    });

    observer.observe(sentinelEl);
    return () => observer.disconnect();
  }, [sentinelEl, observerCallback]);

  const selectOrder = (order: IOrderResponse) => {
    setSelectedOrder(order);
    setActiveContent(MyOrdersContentTypes.ORDER_ITEM);
  };

  const goBackToList = () => {
    setActiveContent(MyOrdersContentTypes.ORDER_LIST);
    setSelectedOrder(null);
  };

  return {
    phone,
    orders,
    isFetching,
    isError,
    hasNext,
    sentinelRef,
    reset: () => {
      setPage(1);
      setOrders([]);
      setHasNext(true);
    },
    activeContent,
    setActiveContent,
    page,
    selectedOrder,
    selectOrder,
    goBackToList,
  };
};

export default useMyOrdersHook;
